import { Badge, Icon } from '@/components/ui';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

import { interviewStanding } from './reschedule';

const STANDING_ICONS = {
  requested: 'repeat',
  declined: 'alert',
  confirmed: 'check',
  awaiting: 'clock',
};

/** Where a booked round stands, as one badge; nothing once it is closed. */
export function InterviewStandingBadge({ interview, className }) {
  const standing = interviewStanding(interview);
  if (!standing.label) return null;
  return (
    <Badge tone={standing.tone} className={className}>
      <Icon name={STANDING_ICONS[standing.key]} size={11} className="shrink-0" />
      {standing.label}
    </Badge>
  );
}

/**
 * The line under a booked round that says what it is waiting for: the
 * candidate's "can't make it" and their reason (prominent, warning), or when
 * the invitation went out and is still unanswered.
 */
export function InterviewStandingNote({ interview, className }) {
  const standing = interviewStanding(interview);
  if (standing.key === 'declined') {
    return (
      <div
        role="note"
        className={cn(
          'rounded-input border-warning-200 bg-warning-50 text-warning-900 mt-3 flex gap-2 border p-3 text-sm',
          className,
        )}
      >
        <Icon name="alert" size={16} className="text-warning-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">The candidate can’t make it</p>
          {standing.detail ? (
            <p className="mt-0.5 whitespace-pre-line">“{standing.detail}”</p>
          ) : (
            <p className="mt-0.5">They didn’t give a reason.</p>
          )}
          <p className="text-warning-800 mt-1 text-xs">
            Choose a new time (they’ll be asked to confirm it) or cancel the interview.
          </p>
        </div>
      </div>
    );
  }
  if (standing.key === 'awaiting' && !standing.started) {
    return (
      <p className={cn('text-info-700 mt-2 flex items-center gap-1.5 text-xs', className)}>
        <Icon name="clock" size={12} className="shrink-0" />
        Waiting for the candidate’s response
        {standing.sentAt && <> · sent {formatDate(standing.sentAt)}</>}
      </p>
    );
  }
  return null;
}
